import axios, { AxiosResponse, AxiosError } from 'axios'
import { useAuthStore } from '@/store/authStore'
import { ApiResponse } from '@/types'

// Configuration API
// Support runtime configuration via window.config (injected by Docker)
const runtimeConfig = (window as any).config || {};
const API_BASE_URL = runtimeConfig.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '/api'
const API_VERSION = runtimeConfig.VITE_API_VERSION || import.meta.env.VITE_API_VERSION || 'v1'

// Instance Axios
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Intercepteur pour gérer les réponses et erreurs
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // 403 + code métier de compte désactivé (suspension/résiliation par AG).
    // On purge la session et redirige vers /login avec le motif en query
    // pour un message contextualisé. Pas de retry refresh — c'est définitif.
    const data = error.response?.data as { code?: string } | undefined
    if (
      error.response?.status === 403 &&
      (data?.code === 'ACCOUNT_SUSPENDED' ||
        data?.code === 'ACCOUNT_INACTIVE' ||
        data?.code === 'ACCOUNT_NOT_ACTIVE')
    ) {
      useAuthStore.getState().logout()
      const reason =
        data.code === 'ACCOUNT_SUSPENDED' ? 'suspended'
        : data.code === 'ACCOUNT_INACTIVE' ? 'terminated'
        : 'inactive'
      // Évite la boucle si on est déjà sur /login.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = `/login?reason=${reason}`
      }
      return Promise.reject(error)
    }

    // Si erreur 401 et pas déjà tenté de rafraîchir le token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        const refreshToken = useAuthStore.getState().refreshToken
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/${API_VERSION}/auth/refresh`, {
            refreshToken
          })
          
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data
          
          useAuthStore.getState().setTokens(newAccessToken, newRefreshToken)
          
          // Retry la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // Si le refresh token est invalide, déconnecter l'utilisateur
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

// Types pour les erreurs API
export interface ApiError {
  message: string
  code?: string
  field?: string
  details?: any
}

// Fonction utilitaire pour extraire les erreurs
export function extractApiError(error: any): ApiError {
  if (error.response?.data) {
    const { data } = error.response
    return {
      message: data.message || 'Une erreur est survenue',
      code: data.code,
      field: data.field,
      details: data.errors || data.details
    }
  }
  
  if (error.message) {
    return {
      message: error.message
    }
  }
  
  return {
    message: 'Une erreur réseau est survenue'
  }
}

// Hook personnalisé pour les appels API avec gestion d'erreurs
export function useApiCall<T = any>() {
  const handleApiCall = async (
    apiCall: () => Promise<AxiosResponse<ApiResponse<T>>>
  ): Promise<T> => {
    try {
      const response = await apiCall()
      
      if (response.data.success) {
        return response.data.data as T
      } else {
        throw new Error(response.data.message || 'Une erreur est survenue')
      }
    } catch (error) {
      const apiError = extractApiError(error)
      throw apiError
    }
  }
  
  return { handleApiCall }
}

// Types pour les paramètres de pagination
export interface PaginationOptions {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// Fonction utilitaire pour construire les paramètres de requête
export function buildQueryParams(params: Record<string, any>): string {
  const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        acc[key] = value.join(',')
      } else {
        acc[key] = value.toString()
      }
    }
    return acc
  }, {} as Record<string, string>)
  
  return new URLSearchParams(filteredParams).toString()
}

// Fonction pour upload de fichiers
export async function uploadFile(
  file: File,
  endpoint: string,
  onProgress?: (progress: number) => void
): Promise<any> {
  const formData = new FormData()
  formData.append('file', file)
  
  try {
    const response = await apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          onProgress(progress)
        }
      },
    })
    
    return response.data
  } catch (error) {
    throw extractApiError(error)
  }
}

// Fonction pour télécharger des fichiers
export async function downloadFile(url: string, filename: string): Promise<void> {
  try {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    
    // Nettoyage
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    throw extractApiError(error)
  }
}

// Headers communs pour les requêtes
export const commonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
}

// Configuration pour les uploads
export const uploadHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'multipart/form-data',
}

export default apiClient